import { Flex, Form, Input, Switch, Modal, Tag } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { FormWrapper, StyledButton } from "../../components/styles";
import { Description, GeneralSettingsContainer, MainFormWrapper, Title } from "./styles";
import { selectSchedulerSettings } from "../../redux/features/schedulerSettings/schedulerSettingsSlice";
import {
    fetchSchedulerInterval,
    runUnpaidCleanupNow,
    updateSchedulerInterval,
} from "../../redux/features/schedulerSettings/schedulerSettingsAsyncThunk";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { RequestResults } from "../../redux/common/RequestStatus";

const UnpaidCleanupSettings: React.FC = () => {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const { data, loading, cleanupRunning } = useAppSelector(selectSchedulerSettings);

    useEffect(() => {
        dispatch(fetchSchedulerInterval());
    }, [dispatch]);

    useEffect(() => {
        form.setFieldsValue({
            unpaidCleanupEnabled: !!data.unpaidCleanupEnabled,
            incompleteDraftDays: data.incompleteDraftDays ?? 7,
            unpaidClaimDays: data.unpaidClaimDays ?? 30,
            recentPaymentHighlightHours: data.recentPaymentHighlightHours ?? 26,
        });
    }, [data, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            await dispatch(updateSchedulerInterval(values));
        } catch (err) {
            console.error("Validation failed:", err);
        }
    };

    const handleRunNow = () => {
        Modal.confirm({
            title: "Run cleanup now?",
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>
                        This will soft-delete (not hard-delete) all matching claims based on the current thresholds:
                    </p>
                    <ul style={{ paddingLeft: 18 }}>
                        <li>Incomplete drafts older than <b>{data.incompleteDraftDays ?? 7}</b> days</li>
                        <li>Completed-but-unpaid claims older than <b>{data.unpaidClaimDays ?? 30}</b> days</li>
                    </ul>
                    <p style={{ color: "#9b9b9b", fontSize: 12, marginBottom: 0 }}>
                        Rows can be restored from the database (deletedAt column is set; data is preserved).
                    </p>
                </div>
            ),
            okText: "Run now",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
                await dispatch(runUnpaidCleanupNow());
            },
        });
    };

    const lastRun = data.lastCleanupRunAt ? new Date(data.lastCleanupRunAt) : null;

    return (
        <GeneralSettingsContainer>
            <Title>Unpaid Claim Cleanup</Title>
            <Description>
                Automatically soft-deletes abandoned drafts and unpaid completed claims. Soft-deleted rows are kept in the
                database and can be restored by clearing their <code>deletedAt</code> column. Hard deletion is never performed.
            </Description>

            <MainFormWrapper>
                <FormWrapper form={form} layout="vertical">
                    <Form.Item
                        label="Enable Auto-Cleanup"
                        name="unpaidCleanupEnabled"
                        valuePropName="checked"
                        tooltip="Master toggle. When off, the hourly cron will not delete anything. The 'Run cleanup now' button still works regardless."
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        label={<>Incomplete Draft Threshold <span>(days, 0 = disabled)</span></>}
                        name="incompleteDraftDays"
                        rules={[
                            { required: true, message: "Please enter a number of days" },
                            {
                                validator: (_, value) => {
                                    if (value === undefined || value === "") return Promise.resolve();
                                    const n = Number(value);
                                    if (isNaN(n) || n < 0 || n > 3650) {
                                        return Promise.reject("Enter a number between 0 and 3650 (10 years)");
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                        tooltip="Soft-delete claims with isDraft=true older than this. Uses draftSavedAt (falls back to createdAt). Set 0 to skip this category."
                    >
                        <Input placeholder="7" size="large" type="number" min={0} max={3650} />
                    </Form.Item>

                    <Form.Item
                        label={<>Completed-but-Unpaid Threshold <span>(days, 0 = disabled)</span></>}
                        name="unpaidClaimDays"
                        rules={[
                            { required: true, message: "Please enter a number of days" },
                            {
                                validator: (_, value) => {
                                    if (value === undefined || value === "") return Promise.resolve();
                                    const n = Number(value);
                                    if (isNaN(n) || n < 0 || n > 3650) {
                                        return Promise.reject("Enter a number between 0 and 3650 (10 years)");
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                        tooltip="Soft-delete claims with isDraft=false and NO successful payment, older than this. Set 0 to skip this category."
                    >
                        <Input placeholder="30" size="large" type="number" min={0} max={3650} />
                    </Form.Item>

                    <Form.Item
                        label={<>Recent Payment Highlight <span>(hours, 0 = disabled)</span></>}
                        name="recentPaymentHighlightHours"
                        rules={[
                            { required: true, message: "Please enter a number of hours" },
                            {
                                validator: (_, value) => {
                                    if (value === undefined || value === "") return Promise.resolve();
                                    const n = Number(value);
                                    if (isNaN(n) || n < 0 || n > 720) {
                                        return Promise.reject("Enter a number between 0 and 720 (30 days)");
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                        tooltip="Two effects: (1) cards whose Paid time is within this window render with a red border + light red background so admins notice new payments; (2) Review claims past this window auto-move to In Process silently (no customer email). Set 0 to disable both."
                    >
                        <Input placeholder="26" size="large" type="number" min={0} max={720} />
                    </Form.Item>

                    <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 6, padding: 12, marginTop: 8 }}>
                        <div style={{ fontSize: 13, color: "#595959", marginBottom: 4 }}>
                            <b>Last run:</b>{" "}
                            {lastRun ? (
                                <>
                                    {lastRun.toLocaleString()} &nbsp;
                                    <Tag color={(data.lastCleanupDeletedCount ?? 0) > 0 ? "orange" : "default"}>
                                        {data.lastCleanupDeletedCount ?? 0} soft-deleted
                                    </Tag>
                                </>
                            ) : (
                                <span style={{ color: "#9b9b9b" }}>never</span>
                            )}
                        </div>
                        <div style={{ fontSize: 12, color: "#9b9b9b" }}>
                            The cron checks hourly. When enabled, it runs at the top of every hour and processes anything past the day thresholds above.
                        </div>
                    </div>
                </FormWrapper>
            </MainFormWrapper>

            <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
                <StyledButton
                    onClick={handleRunNow}
                    loading={cleanupRunning === RequestResults.pending}
                    danger
                >
                    Run Cleanup Now
                </StyledButton>
                <StyledButton
                    type="primary"
                    onClick={handleSave}
                    loading={loading === RequestResults.pending}
                >
                    Save Settings
                </StyledButton>
            </Flex>
        </GeneralSettingsContainer>
    );
};

export default UnpaidCleanupSettings;
